import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import { 
  Layout, 
  Card, 
  Input, 
  Button, 
  Table, 
  Typography, 
  Modal, 
  Space, 
  Avatar, 
  Tag, 
  Pagination,
  Spin,
  Alert,
  Tooltip
} from 'antd';
import { 
  DownloadOutlined, 
  CalendarOutlined, 
  TeamOutlined, 
  ClockCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { sarabunFont } from '../../sarabunFont';
import './Classes.css';

const { Content } = Layout;
const { Title, Text } = Typography;

// Keeping the original font loading function
const loadSarabunFont = (doc) => {
  doc.addFileToVFS('Sarabun.ttf', sarabunFont.src.split('base64,')[1]);
  doc.addFont('Sarabun.ttf', 'Sarabun', 'normal');
  doc.setFont('Sarabun');
};

const CurrentClasses = () => {
  const { t } = useTranslation();
  const [classGroups, setClassGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [popupData, setPopupData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 30;
  const API_URL = process.env.REACT_APP_API_URL;

  // Keeping all the original utility functions
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
  
      const day = date.getDate().toString().padStart(2, '0'); // always 2 digits
      const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase(); // force English, 3-letter month
      const year = date.getFullYear();
  
      return `${day} ${month} ${year}`;
    } catch (error) {
      console.error('Invalid date value:', dateString);
      return 'Invalid Date';
    }
  };
  

  const formatTime = (timeString) => {
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    } catch (error) {
      console.error('Invalid time value:', timeString);
      return 'Invalid Time';
    }
  };

  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}:${remainingMinutes.toString().padStart(2, '0')} ${t('hrs')}`;
  };

  const formatScheduleHour = (scheduleHourString) => {
    try {
      const [hours, minutes] = scheduleHourString.split(' ').map((value) => parseInt(value, 10));
      return `${hours}:${minutes.toString().padStart(2, '0')} ${t('hrs')}`;
    } catch (error) {
      console.error('Invalid schedule hour value:', scheduleHourString);
      return 'Invalid Time';
    }
  };

  const getDayOfWeek = (dateString) => {
    const date = new Date(dateString);
    const daysOfWeek = t('daysOfWeek', { returnObjects: true });
    return daysOfWeek[date.getDay()];
  };

  // API calls remain the same
  const fetchClassGroups = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/class-groups`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setClassGroups(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching class groups:', error);
      setError(t('fetchClassGroupsError'));
      setLoading(false);
    }
  }, [API_URL, t]);


  useEffect(() => {
    fetchClassGroups();
  }, [fetchClassGroups]);

  // Schedule modal handlers
  const handleScheduleClick = (schedule) => {
    const sortedSchedule = [...schedule].sort((a, b) => new Date(a.date) - new Date(b.date));
    setPopupData(sortedSchedule);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    loadSarabunFont(doc);
  
    const margin = 20;
    const keyColor = '#7DDCC0';
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = pageWidth / 2;
  
    const headerImage = new Image();
    headerImage.src = '/images/keaes150.png';
  
    headerImage.onload = () => {
      // Calculate aspect ratio for logo
      const imgAspectRatio = headerImage.width / headerImage.height;
      const targetHeight = 20;
      const targetWidth = targetHeight * imgAspectRatio;
  
      // Add logo maintaining aspect ratio
      doc.addImage(headerImage, 'PNG', margin, margin, targetWidth, targetHeight);
  
      // Class details
      const classCode = popupData[0]?.class_code || 'N/A';
      const totalHours = popupData?.[0]?.total_hours || 0;
  
      // Large class code with more space and key color
      doc.setFontSize(28);
      doc.setTextColor(keyColor);
      doc.setFont('helvetica', 'bold');
      doc.text(classCode, centerX, margin + 40, { align: 'center' });
  
      // Get first and last dates from sorted data
      const sortedDates = popupData
        .map(item => new Date(item.date))
        .sort((a, b) => a - b);
      const startDate = formatDate(sortedDates[0]);
      const endDate = formatDate(sortedDates[sortedDates.length - 1]);
  
      // Display dates with larger font and bold
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(51, 51, 51);
      doc.text(`Start Date: ${startDate}`, margin, margin + 60);
      doc.text(`End Date: ${endDate}`, centerX + 20, margin + 60);
      doc.text(`Total Hours: ${formatMinutesToHours(totalHours)}`, margin, margin + 70);
  
      // Subject line with key color
      const subjects = [...new Set(popupData.map(slot => slot.subject_name))];
      doc.setFontSize(12);
      doc.setTextColor(keyColor);
      
      // Handle long subject list with word wrapping
      const subjectsText = `Subject(s): ${subjects.join(', ')}`;
      const maxLineWidth = pageWidth - (2 * margin);
      const splitSubjects = doc.splitTextToSize(subjectsText, maxLineWidth);
      
      // Add each line of subjects text
      splitSubjects.forEach((line, index) => {
        doc.text(line, margin, margin + 85 + (index * 6));
      });
  
      // Calculate table starting position based on number of subject lines
      const tableStartY = margin + 95 + (splitSubjects.length * 6);
  
      // Sort popupData by date and time
      const sortedPopupData = popupData.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() === dateB.getTime() 
          ? a.schedule_time.localeCompare(b.schedule_time)
          : dateA - dateB;
      });
  
      // Prepare table data
      const columns = [
        { header: 'DAY', dataKey: 'schedule_day' },
        { header: 'DATE', dataKey: 'date' },
        { header: 'TIME', dataKey: 'schedule_time' },
        { header: 'SUBJECT', dataKey: 'subject_name' },
        { header: 'HOURS', dataKey: 'schedule_hour' },
        { header: 'TUTOR', dataKey: 'nickname' },
      ];
  
      const rows = sortedPopupData.map(slot => ({
        schedule_day: getDayOfWeek(slot.date),
        date: formatDate(slot.date),
        schedule_time: `${formatTime(slot.schedule_time)} - ${formatTime(slot.end_time)}`,
        subject_name: slot.subject_name,
        schedule_hour: formatScheduleHour(slot.schedule_hour),
        nickname: slot.nickname
      }));
  
      // Add table with styled settings like the second example
      doc.autoTable({
        startY: tableStartY,
        head: [columns.map(col => col.header)],
        body: rows.map(row => columns.map(col => row[col.dataKey])),
        theme: 'grid',
        styles: {
          font: 'Sarabun',
          fontStyle: 'normal',
          textColor: [51, 51, 51],
          lineColor: [200, 200, 200],
          fontSize: 10
        },
        columnStyles: {
          schedule_day: { cellWidth: 25 },
          date: { cellWidth: 30 },
          schedule_time: { cellWidth: 45 },
          subject_name: { cellWidth: 60 },
          schedule_hour: { cellWidth: 25 }
        },
        headStyles: {
          fillColor: [149, 16, 30], // Dark red header from second example
          textColor: [255, 255, 255],
          fontSize: 11,
          fontStyle: 'bold',
          halign: 'left',
          valign: 'middle'
        },
        alternateRowStyles: {
          fillColor: [252, 252, 252] // Light gray for alternate rows
        },
        margin: { left: margin, right: margin },
        didParseCell: function(data) {
          if (data.cell.raw === null || data.cell.raw === undefined) {
            data.cell.text = '-'; // Replace empty cells with dash
          }
        },
        rowSpacing: 2, // Add more space between rows
      });
  
      // Add page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for(let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setTextColor(128, 128, 128);
        doc.setFontSize(8);
        doc.text(
          `Page ${i} of ${pageCount}`, 
          doc.internal.pageSize.getWidth() / 2, 
          doc.internal.pageSize.getHeight() - 10, 
          { align: 'center' }
        );
      }
  
      // Save the PDF
      doc.save(`${classCode}.pdf`);
    };
  
    headerImage.onerror = () => {
      console.error('Failed to load the header image.');
    };
  };

  // Data processing
  const currentDate = new Date();
  const sortedClassGroups = classGroups
    .filter(cls => cls.isprivate === false && new Date(cls.end_date) >= currentDate)
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

  const filteredClassGroups = sortedClassGroups.filter(cls =>
    cls.class_code.toLowerCase().includes(searchTerm.toLowerCase())
  );


  // Table columns configuration for the schedule modal
  const scheduleColumns = [
    {
      title: t('day'),
      dataIndex: 'date',
      key: 'day',
      render: (date) => getDayOfWeek(date),
    },
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      render: (date) => formatDate(date),
    },
    {
      title: t('subjectName'),
      dataIndex: 'subject_name',
      key: 'subject_name',
    },

    {
      title: t('time'),
      key: 'time',
      render: (_, record) => `${formatTime(record.schedule_time)} - ${formatTime(record.end_time)}`,
    },
    {
      title: t('hours'),
      dataIndex: 'schedule_hour',
      key: 'hours',
      render: (hours) => formatScheduleHour(hours),
    },
    {
      title: t('teacher'),
      dataIndex: 'nickname',
      key: 'nickname',
    },
  ];


  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message={error} type="error" />;
  }

  return (
<Layout>
  <Sidebar />
  <Layout>
    <Header />
    <Content 
      style={{ 
        marginTop: '50px', 
        padding: '24px',
        minHeight: '280px',
        background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)'
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Title 
          level={2}
          style={{
            textAlign: 'center',
            marginBottom: 40,
            color: '#1a365d',
            position: 'relative'
          }}
        >
          {t('ourCourses')}
        </Title>
        
        <Space direction="vertical" size="large" style={{ width: '100%', marginBottom: 24 }}>
          {/* Search and Pagination Container */}
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: 24
          }}>
            <Input
              prefix={<SearchOutlined />}
              placeholder={t('searchByClassCode')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ 
                maxWidth: 300,
                marginBottom: 16
              }}
            />
            
            <Pagination
              current={currentPage}
              total={filteredClassGroups.length}
              pageSize={itemsPerPage}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
              style={{ 
                textAlign: 'center',
                marginTop: 16
              }}
            />
          </div>

          {/* Cards Grid */}
          <div style={{ 
            display: 'grid', 
            gap: '24px', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))'
          }}>
            {filteredClassGroups
              .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
              .map((cls) => (
                <Card
                  key={cls.groupid}
                  hoverable
                  style={{
                    background: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                  actions={[
                    <Tooltip title={t('viewSchedule')}>
                      <Button 
                        type="link" 
                        icon={<CalendarOutlined />}
                        onClick={() => handleScheduleClick(cls.classes)}
                      >
                        {t('viewSchedule')}
                      </Button>
                    </Tooltip>
                  ]}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space align="center">
                      <Avatar 
                        style={{ 
                          backgroundColor: ['#f56a00', '#7265e6', '#ffbf00'][cls.groupid % 3],
                          verticalAlign: 'middle',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        size={40}
                      >
                        {cls.class_code.charAt(0)}
                      </Avatar>
                      <Title level={4} style={{ margin: 0 }}>{cls.class_code}</Title>
                    </Space>

                    <div style={{
                      background: '#f8f9fa',
                      padding: '16px',
                      borderRadius: '6px',
                      marginTop: '12px'
                    }}>
                      <Space direction="vertical" size={8}>
                        <Text type="secondary">
                          <CalendarOutlined style={{ marginRight: 8 }} />
                          {formatDate(cls.start_date)} - {formatDate(cls.end_date)}
                        </Text>
                        <Text>
                          <ClockCircleOutlined style={{ marginRight: 8 }} />
                          {t('totalHours')}: {formatMinutesToHours(cls.total_hours)}
                        </Text>
                        <Text>
                          <TeamOutlined style={{ marginRight: 8 }} />
                          {t('maxRegister')}: {cls.register_capacity}
                        </Text>

                        <Tag 
                          color={cls.isprivate ? 'purple' : 'blue'}
                          style={{ marginTop: 8 }}
                        >
                          {cls.isprivate ? t('private') : t('group')}
                        </Tag>
                      </Space>
                    </div>
                  </Space>
                </Card>
            ))}
          </div>
        </Space>

        {/* Modal */}
        <Modal
          title={
            <div style={{ 
              borderBottom: '1px solid #f0f0f0',
              padding: '16px 0',
              margin: '0 -24px 16px',
              paddingLeft: 24
            }}>
              {popupData?.[0]?.class_code || t('scheduleDetails')}
            </div>
          }
          open={!!popupData}
          onCancel={() => setPopupData(null)}
          width={900}
          footer={[
            <Button 
              key="download" 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={handleDownloadPDF}
            >
              {t('downloadPDF')}
            </Button>,
            <Button key="close" onClick={() => setPopupData(null)}>
              {t('close')}
            </Button>
          ]}
        >
          <Table
            columns={scheduleColumns}
            dataSource={[...(popupData ?? [])].sort((a, b) => {
              const dateA = new Date(a.date);
              const dateB = new Date(b.date);

              if (isNaN(dateA) || isNaN(dateB)) return 0;

              if (dateA.getTime() !== dateB.getTime()) {
                return dateA - dateB;
              }

              const timeA = a.schedule_time ? a.schedule_time.split(":").map(Number) : [0, 0];
              const timeB = b.schedule_time ? b.schedule_time.split(":").map(Number) : [0, 0];

              const hourDiff = timeA[0] - timeB[0];
              return hourDiff !== 0 ? hourDiff : timeA[1] - timeB[1];
            })}
            rowKey={(record, index) => index}
            pagination={false}
            scroll={{ x: true }}
            style={{
              background: 'white',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          />
        </Modal>
      </div>
    </Content>
  </Layout>
</Layout>
  );
};

export default withRoleAccess(CurrentClasses, ['admin', 'superadmin', 'teacher', 'student']);